import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import Card from 'assets/components/card/Card';
import ThemeCardListItem from 'assets/components/card/themes/Theme.Card.ListItem';
import DataMap from 'assets/components/dataMap/DataMap';
import { useModalForm } from 'assets/components/form/modalForm/ModalForm';
import FormatValue from 'assets/components/formatValue/FormatValue';
import Icon from 'assets/components/icon/Icon';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import ErrorWrapper from 'assets/components/inputs/wrappers/ErrorWrapper';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { HttpStatus } from 'config/Api.Config';
import { isEmpty, values } from 'lodash';
import contractRefundPricingInfoApi from 'models/sales/contractRefund/refundPricingInfo/Api.ContractRefund.RefundPricingInfo';
import Contract from 'models/sales/contract/Model.Contract';
import ContractCancellation from 'models/sales/contractCancellation/Model.ContractCancellation';
import { ContractRefundSummariesTable } from 'module/sales/views/contractRefund/components/ContractRefundSummariesTable';
import {
  RefundDistributionTable,
  RefundSubfeeTable,
} from 'module/sales/views/contractRefund/components/RefundDistributionTable';
import RefundInfoTable from 'module/sales/views/contractRefund/components/RefundInfoTable';
import { useEffect, useMemo, useState } from 'react';
import { useSalesContractProvider } from '../../../Sales.Contract.Hooks';
import { ContractRefundProcessForm } from '../Contract.RefundProcess';

const cardStyles = {
  borderRadius: Theme.Size.S,
  cardMedia: {
    width: 0,
  },
  cardInfo: {
    paddingRight: Theme.Size.M,
  },
};
const wrapperContainerStyles: React.CSSProperties = {
  display: 'grid',
  flexWrap: 'wrap',
  gap: `${Theme.Size.L} ${Theme.Size.L}`,
  gridTemplateColumns: 'repeat(3, 1fr)',
};
const containerStyles: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: Theme.Size.S,
};
const dividerStyles: React.CSSProperties = {
  gridColumn: '1 / -1',
};

type RefundPricingInfo = Api.Sales.ContractRefund.RefundPricingInfo.RefundPriceInfo;
type Props = {
  refundPricingInfo: RefundPricingInfo;
  setRefundPricingInfo: React.Dispatch<React.SetStateAction<RefundPricingInfo>>;
};

const ContractRefundProcessRefund = ({ refundPricingInfo, setRefundPricingInfo }: Props) => {
  const { form } = useModalForm<ContractRefundProcessForm>();
  const { lang } = getLocales();
  const { pageRouter, refundConfigTemplateListApi, selected } = useSalesContractProvider();

  const refundConfigTemplates = useMemo(
    () =>
      refundConfigTemplateListApi?.payload?.data?.filter(
        (it) => !selected?.productId || it.products?.some((product) => product.id === selected.productId)
      ),
    [refundConfigTemplateListApi?.payload?.data, selected?.productId]
  );

  useEffect(() => {
    if (form.data?.refundConfigTemplateId && form.data?.cancellationDateLocal) {
      (async () => {
        setRefundPricingInfo(undefined);
        const res = await contractRefundPricingInfoApi(
          {
            contractId: parseInt(pageRouter.params.id),
          },
          {
            refundConfigTemplateId: form.data?.refundConfigTemplateId,
            contractCancellation: {
              cancellationDateLocal: form.data?.cancellationDateLocal,
              odometerReading: form.data?.odometerReading,
            },
            areClaimDeductionsApplied: form.data?.areClaimDeductionsApplied,
          }
        );

        if (res.status === HttpStatus.ok) {
          setRefundPricingInfo(res.payload);
          form.update({ ...res.payload });
        }
      })();
    }
  }, [
    form.data?.refundConfigTemplateId,
    form.data?.cancellationDateLocal,
    form.data?.odometerReading,
    form.data?.areClaimDeductionsApplied,
  ]);
  const selectedTemplate = useMemo(
    () => refundConfigTemplates?.find((it) => it.id === form.data?.refundConfigTemplateId),
    [refundConfigTemplates, form.data?.refundConfigTemplateId]
  );

  const getKmOrDateBasedLabel = (data: Api.Sales.ContractRefund.RefundPricingInfo.RefundPriceInfo) => {
    const hasMinimum = data?.refundConfigTemplate?.minimumRetainedPremium;
    const km = data?.remainingOdometerReadingPercentage;
    const days = data?.remainingTermInDaysPercentage;

    if (hasMinimum) {
      if (km && days && km < days) {
        return lang.km;
      }
    } else {
      if (km && data && km === data?.refundFactor) {
        return lang.km;
      }
    }
    return lang.dateBased;
  };

  return (
    <FormWrapper>
      <SelectInput
        name="refundConfigTemplateId"
        label={lang.refundConfigTemplate}
        data={refundConfigTemplates?.map((it) => it.displayInfo)}
        value={form.data?.refundConfigTemplateId}
        error={form.errors?.refundConfigTemplateId}
        onChange={({ refundConfigTemplateId }) => {
          const refundConfigTemplate = refundConfigTemplates?.find((it) => it.id === refundConfigTemplateId);
          form.update({
            refundConfigTemplateId,
            refundConfigTemplate,
            refundConfigTemplateJson: refundConfigTemplate,
          });
        }}
        icon={<Icon class="fas-layer-group" />}
      />
      &nbsp;
      {refundPricingInfo ? (
        <>
          <Card
            subtitle={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-evenly' }}>
                <div>
                  <p>
                    <strong>{lang.nameOfTemplateInvoked}:</strong>
                  </p>
                  <p>{selectedTemplate?.internalName ?? 'N/A'}</p>
                </div>
                <div>
                  <p>
                    <strong>{lang.minimumRetainedPremium}:</strong>
                  </p>
                  <p>{!selectedTemplate ? 'N/A' : selectedTemplate?.minimumRetainedPremium * 100}%</p>
                </div>
                <div>
                  <p>
                    <strong>{lang.kmOrDateBased}:</strong>
                  </p>
                  <p>{!refundPricingInfo ? 'N/A' : getKmOrDateBasedLabel(refundPricingInfo)}</p>
                </div>
              </div>
            }
            class={ThemeCardListItem}
            style={cardStyles}
          />
          &nbsp;
          <RefundPricingAdditionalInfo
            data={refundPricingInfo}
            contract={selected}
            contractCancellation={new ContractCancellation(form.data)}
          />
          &nbsp;
          <ContractRefundSummariesTable data={refundPricingInfo} />
        </>
      ) : (
        <ErrorWrapper children={`${lang.loading}...`} style={{ justifySelf: 'center' }} />
      )}
    </FormWrapper>
  );
};

const DisplayInfoBox = ({ label, children }: { label: string; children: React.ReactNode }) => {
  return (
    <p style={{ margin: 0 }}>
      <span>
        {label}: <strong>{children}</strong>
      </span>
    </p>
  );
};
const RefundPricingAdditionalInfo = ({
  data,
  contract,
  contractCancellation,
}: {
  data: RefundPricingInfo;
  contract: Contract;
  contractCancellation: ContractCancellation;
}) => {
  const { lang } = getLocales();
  const [showDistributions, setShowDistributions] = useState<boolean>(false);
  return (
    <div>
      <RefundInfoTable refundPriceInfo={data} contract={contract} contractCancellation={contractCancellation} />
      <div style={wrapperContainerStyles}>
        <hr style={dividerStyles} />
        <div style={containerStyles}>
          <DisplayInfoBox
            label={lang.dealerCostValueAtSale}
            children={<FormatValue value={`$${contract?.dealerCost ?? 0}`} />}
          />
          <DisplayInfoBox
            label={lang.dealerCostTaxAtSale}
            children={<FormatValue value={`$${data.dealerCostTaxAtSale ?? 0}`} />}
          />
        </div>
        <div style={containerStyles}>
          <DisplayInfoBox
            label={lang.retainedDealerCostAmt}
            children={<FormatValue value={`$${data.retainedDealerCostAmt ?? 0}`} />}
          />
          <DisplayInfoBox
            label={lang.retainedDealerCostTaxAmt}
            children={<FormatValue value={`$${data.retainedDealerCostTaxAmt ?? 0}`} />}
          />
        </div>
        <div style={containerStyles}>
          <DisplayInfoBox
            label={lang.refundedDealerCostAmt}
            children={<FormatValue value={`$${data.refundedDealerCostAmt ?? 0}`} />}
          />
          <DisplayInfoBox
            label={lang.refundedDealerCostTaxAmt}
            children={<FormatValue value={`$${data.refundedDealerCostTaxAmt ?? 0}`} />}
          />
        </div>
        <hr style={dividerStyles} />
        <div style={containerStyles}>
          <DisplayInfoBox
            label={lang.dealerProfitValueAtSale}
            children={<FormatValue value={`$${contract?.dealerProfit ?? 0}`} />}
          />
          <DisplayInfoBox
            label={lang.dealerProfitTaxAtSale}
            children={<FormatValue value={`$${data.dealerProfitTaxAtSale ?? 0}`} />}
          />
        </div>
        <div style={containerStyles}>
          <DisplayInfoBox
            label={lang.retainedASCDealerProfitAmt}
            children={<FormatValue value={`$${data.retainedASCDealerProfitAmt ?? 0}`} />}
          />
          <DisplayInfoBox
            label={lang.retainedASCDealerProfitTaxAmt}
            children={<FormatValue value={`$${data.retainedASCDealerProfitTaxAmt ?? 0}`} />}
          />
        </div>
        <div style={containerStyles}>
          <DisplayInfoBox
            label={lang.refundedASCDealerProfitAmt}
            children={<FormatValue value={`$${data.refundedASCDealerProfitAmt ?? 0}`} />}
          />
          <DisplayInfoBox
            label={lang.refundedASCDealerProfitTaxAmt}
            children={<FormatValue value={`$${data.refundedASCDealerProfitTaxAmt ?? 0}`} />}
          />
        </div>
        <hr style={dividerStyles} />
        <div style={containerStyles}>
          <DisplayInfoBox
            label={lang.dealerProfitValueAtSale}
            children={<FormatValue value={`$${contract?.dealerProfit ?? 0}`} />}
          />
          <DisplayInfoBox
            label={lang.dealerProfitTaxAtSale}
            children={<FormatValue value={`$${data.dealerProfitTaxAtSale ?? 0}`} />}
          />
        </div>
        <div style={containerStyles}>
          <DisplayInfoBox
            label={lang.retainedDealerProfitAmt}
            children={<FormatValue value={`$${data.retainedDealerProfitAmt ?? 0}`} />}
          />
          <DisplayInfoBox
            label={lang.retainedDealerProfitTaxAmt}
            children={<FormatValue value={`$${data.retainedDealerProfitTaxAmt ?? 0}`} />}
          />
        </div>
        <div style={containerStyles}>
          <DisplayInfoBox
            label={lang.refundedDealerProfitAmt}
            children={<FormatValue value={`$${data.refundedDealerProfitAmt ?? 0}`} />}
          />
          <DisplayInfoBox
            label={lang.refundedDealerProfitTaxAmt}
            children={<FormatValue value={`$${data.refundedDealerProfitTaxAmt ?? 0}`} />}
          />
        </div>
        <hr style={dividerStyles} />
        <div style={containerStyles}>
          <DisplayInfoBox
            label={lang.retailSalePriceValueAtSale}
            children={<FormatValue value={`$${contract?.salePriceCAD ?? 0}`} />}
          />
          <DisplayInfoBox
            label={lang.retailSalePriceTaxValueAtSale}
            children={<FormatValue value={`$${data.retailSalePriceTaxValueAtSale ?? 0}`} />}
          />
        </div>
        <div style={containerStyles}>
          <DisplayInfoBox
            label={lang.totalRetainedDealerAmt}
            children={<FormatValue value={`$${data.totalRetainedDealerAmt ?? 0}`} />}
          />
          <DisplayInfoBox
            label={lang.totalRetainedDealerTaxAmt}
            children={<FormatValue value={`$${data.totalRetainedDealerTaxAmt ?? 0}`} />}
          />
        </div>
        <div style={containerStyles}>
          <DisplayInfoBox
            label={lang.totalRefundedDealerAmt}
            children={<FormatValue value={`$${data.totalRefundedDealerAmt ?? 0}`} />}
          />
          <DisplayInfoBox
            label={lang.totalRefundedDealerTaxAmt}
            children={<FormatValue value={`$${data.totalRefundedDealerTaxAmt ?? 0}`} />}
          />
        </div>
        <hr style={dividerStyles} />
        <div></div>
        <div style={containerStyles}>
          <DisplayInfoBox
            label={lang.totalRetainedSubtotal}
            children={<FormatValue value={`$${data.totalRetainedDealerSubtotal ?? 0}`} />}
          />
        </div>
        <div style={containerStyles}>
          <DisplayInfoBox
            label={lang.totalRefundedSubtotal}
            children={<FormatValue value={`$${data.totalRefundedDealerSubtotal ?? 0}`} />}
          />
          <DisplayInfoBox
            label={lang.administrationFee}
            children={<FormatValue value={`$${data.totalDeductions ?? 0}`} />}
          />
          <DisplayInfoBox
            label={lang.administrationFeeTax}
            children={<FormatValue value={`$${data.totalDeductionsTaxAmount ?? 0}`} />}
          />
        </div>
        <hr style={dividerStyles} />
        <div></div>
        <div></div>
        <div style={containerStyles}>
          <DisplayInfoBox
            label={lang.totalToBeRefunded}
            children={<FormatValue value={`$${data.totalToBeRefunded ?? 0}`} />}
          />
        </div>
      </div>
      &nbsp;
      <Button
        onClick={() => setShowDistributions((prev) => !prev)}
        data={showDistributions}
        class={ThemeButtonCircle}
        media={<Icon class={showDistributions ? 'fas-chevron-up' : 'fas-chevron-down'} />}
        htmlElementProps={{
          title: showDistributions ? lang.viewLess : lang.viewMore,
        }}
        render={showDistributions ? lang.viewLess : lang.viewMore}
        active={showDistributions}
      />
      {showDistributions && (
        <>
          <h1>{lang.distributions}</h1>
          {!!values(data?.pricingDistributions).length && (
            <RefundDistributionTable title={lang.pricingDistributions} distributions={data?.pricingDistributions} />
          )}
          {!!values(data?.distributorDistributions).length && (
            <RefundDistributionTable
              title={lang.distributorDistributions}
              distributions={data?.distributorDistributions}
            />
          )}
          &nbsp;
          {data?.subfees && !isEmpty(data?.subfees) && (
            <>
              <h1>{lang.surcharges}</h1>
              <RefundSubfeeTable subfees={data?.subfees} />
            </>
          )}
          {data?.enhancements && !isEmpty(data?.enhancements) && (
            <>
              <h1>{lang.enhancements}</h1>

              <DataMap
                data={data?.enhancements}
                render={({ data: enhancement }) => {
                  return (
                    <div key={enhancement?.enhancementId}>
                      <h2>{enhancement.name || lang.unknown}:</h2>

                      {!!values(enhancement?.pricingDistributions).length && (
                        <RefundDistributionTable
                          title={lang.pricingDistributions}
                          distributions={enhancement?.pricingDistributions}
                        />
                      )}
                      {!!values(enhancement?.distributorDistributions).length && (
                        <RefundDistributionTable
                          title={lang.distributorDistributions}
                          distributions={enhancement?.distributorDistributions}
                        />
                      )}
                    </div>
                  );
                }}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ContractRefundProcessRefund;
